import React, { useState, useEffect } from "react";
import { Dialog, Typography, Card, CardContent } from "@mui/material";
import {
  XMarkIcon,
  ClockIcon,
  CheckCircleIcon,
} from "@heroicons/react/24/outline";
import { ExplanationRequest, TimeSlot } from "../../../types/explanations";
import { formatDateTime } from "../../../utils";
import { getRequestSlots } from "../../../server/Explanations CRUD/Requests/newRequests";

interface SlotSelectorModalProps {
  request: ExplanationRequest | any;
  onConfirm: (slotId: string) => void;
  onClose: () => void;
}

const SlotSelectorModal: React.FC<SlotSelectorModalProps> = ({
  request,
  onConfirm,
  onClose,
}) => {
  const [selectedSlotId, setSelectedSlotId] = useState<string>("");
  const [proposedSlots, setProposedSlots] = useState<
    {
      _id: string;
      studentRequest: string;
      professor: {
        _id: string;
        firstname: string;
        lastname: string;
        email: string;
      };
      dateTime: string;
      createdAt: string;
    }[]
  >([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Récupérer les créneaux proposés depuis l'API
  useEffect(() => {
    const fetchSlots = async () => {
      if (!request._id) return;

      try {
        setLoading(true);
        const response = await getRequestSlots(request._id);
        console.log("📨 Réponse getRequestSlots:", response);

        if (
          response.status === 200 &&
          response.body &&
          Array.isArray(response.body)
        ) {
          setProposedSlots(response.body);
          setError(null);
        } else {
          setError("Erreur lors de la récupération des créneaux");
        }
      } catch (err) {
        console.error("❌ Erreur fetchSlots:", err);
        setError("Erreur de connexion au serveur");
      } finally {
        setLoading(false);
      }
    };

    fetchSlots();
  }, [request._id]);

  // Formater la date et l'heure
  const formatDateTime = (dateString: string) => {
    const date = new Date(dateString);
    return {
      date: date.toLocaleDateString("fr-FR", {
        weekday: "long",
        day: "numeric",
        month: "long",
      }),
      time: date.toLocaleTimeString("fr-FR", {
        hour: "2-digit",
        minute: "2-digit",
      }),
    };
  };

  // Calculer le temps restant pour choisir (24h)
  const getTimeRemaining = () => {
    const now = new Date();
    const deadline = new Date(request.updatedAt);
    deadline.setHours(deadline.getHours() + 24);

    const diff = deadline.getTime() - now.getTime();

    if (diff <= 0) {
      return { expired: true, text: "Délai expiré" };
    }

    const hours = Math.floor(diff / (1000 * 60 * 60));
    const minutes = Math.floor((diff % (1000 * 60 * 60)) / (1000 * 60));

    return {
      expired: false,
      text: `${hours}h ${minutes}m restantes`,
    };
  };

  const timeRemaining = getTimeRemaining();

  const handleConfirm = () => {
    if (!selectedSlotId || !request._id) return;

    // Appeler directement la fonction onConfirm du parent
    onConfirm(selectedSlotId);
  };

  return (
    <Dialog
      open={true}
      onClose={onClose}
      maxWidth="lg"
      fullWidth
      sx={{ zIndex: 9999 }}
    >
      <div className="bg-white rounded-xl shadow-xl flex flex-col max-h-[90vh] w-full">
        {/* Header */}
        <div className="p-4 sm:p-6 border-b flex-shrink-0 bg-blue-600 text-white rounded-t-xl">
          <div className="flex items-center justify-between">
            <div>
              <h2 className="text-xl sm:text-2xl font-semibold">
                Choisir un créneau
              </h2>
              <p className="text-blue-100 mt-1 text-sm sm:text-base">
                {proposedSlots.length > 0
                  ? `${proposedSlots[0].professor.firstname} ${proposedSlots[0].professor.lastname}`
                  : "Professeur"}{" "}
                - Mathématiques
              </p>
            </div>
            <button
              onClick={onClose}
              className="text-white hover:text-gray-200 transition-colors"
            >
              <XMarkIcon className="w-5 h-5 sm:w-6 sm:h-6" />
            </button>
          </div>
        </div>

        {/* Contenu */}
        <div className="flex-1 overflow-hidden">
          <div className="p-3 sm:p-6 overflow-y-auto max-h-96">
            {/* Timer countdown */}
            <div
              className={`mb-4 sm:mb-6 p-3 sm:p-4 rounded-lg border ${
                timeRemaining.expired
                  ? "bg-red-50 border-red-200"
                  : "bg-yellow-50 border-yellow-200"
              }`}
            >
              <div className="flex items-center">
                <ClockIcon
                  className={`w-4 h-4 sm:w-5 sm:h-5 mr-2 flex-shrink-0 ${
                    timeRemaining.expired ? "text-red-600" : "text-yellow-600"
                  }`}
                />
                <span
                  className={`font-medium text-sm sm:text-base ${
                    timeRemaining.expired ? "text-red-800" : "text-yellow-800"
                  }`}
                >
                  {timeRemaining.text}
                </span>
              </div>
              {!timeRemaining.expired && (
                <p className="text-xs sm:text-sm text-yellow-700 mt-1">
                  Vous avez 24h pour choisir un créneau, sinon votre demande
                  sera annulée.
                </p>
              )}
            </div>

            {/* Rappel du problème */}
            <div className="mb-4 sm:mb-6 p-3 sm:p-4 bg-gray-50 rounded-lg">
              <h3 className="font-medium text-gray-900 mb-2 text-sm sm:text-base">
                Votre demande:
              </h3>
              <p className="text-xs sm:text-sm text-gray-700">
                {request.comment || request.description || "Aucune description"}
              </p>
            </div>

            {/* Informations du professeur */}
            {request.teacherName && (
              <div className="mb-4 sm:mb-6 p-3 sm:p-4 bg-green-50 border border-green-200 rounded-lg">
                <h3 className="font-medium text-green-900 mb-2 text-sm sm:text-base">
                  Professeur assigné:
                </h3>
                <p className="text-xs sm:text-sm text-green-800">
                  {request.teacherName}
                </p>
              </div>
            )}

            {/* Message d'erreur */}
            {error && (
              <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-lg">
                <p className="text-red-700 text-sm">{error}</p>
              </div>
            )}

            {/* Liste des créneaux proposés */}
            <div className="mb-4 sm:mb-6">
              <h3 className="font-medium text-gray-900 mb-3 sm:mb-4 text-sm sm:text-base">
                Créneaux proposés ({proposedSlots.length}/3)
              </h3>

              {loading ? (
                <div className="text-center py-6 sm:py-8 text-gray-500">
                  <div className="animate-spin rounded-full h-8 w-8 border-4 border-blue-500 border-t-transparent mx-auto mb-3"></div>
                  <p className="text-sm">Chargement des créneaux...</p>
                </div>
              ) : proposedSlots.length === 0 ? (
                <div className="text-center py-6 sm:py-8 text-gray-500">
                  <ClockIcon className="w-10 h-10 sm:w-12 sm:h-12 mx-auto mb-3 text-gray-400" />
                  <p className="text-sm">
                    Aucun créneau proposé pour le moment
                  </p>
                </div>
              ) : (
                <div className="space-y-2 sm:space-y-3">
                  {proposedSlots.map((slot: any, index: number) => {
                    const { date, time } = formatDateTime(slot.dateTime);
                    const isSelected = selectedSlotId === slot._id;

                    return (
                      <div
                        key={slot._id}
                        className={`border rounded-lg p-3 sm:p-4 cursor-pointer transition-all ${
                          isSelected
                            ? "border-[#E3B500] bg-yellow-50"
                            : "border-gray-200 hover:border-gray-300 hover:bg-gray-50"
                        }`}
                        onClick={() => setSelectedSlotId(slot._id)}
                      >
                        <div className="flex items-center justify-between">
                          <div className="flex items-center min-w-0 flex-1">
                            <input
                              type="radio"
                              name="slot"
                              value={slot._id}
                              checked={isSelected}
                              onChange={() => setSelectedSlotId(slot._id)}
                              className="mr-2 sm:mr-3 text-[#E3B500] focus:ring-[#E3B500] flex-shrink-0"
                            />
                            <div className="min-w-0">
                              <p className="font-medium text-gray-900 capitalize text-sm sm:text-base truncate">
                                {date}
                              </p>
                              <p className="text-xs sm:text-sm text-gray-600">
                                {time} - 1 heure
                              </p>
                            </div>
                          </div>

                          {isSelected && (
                            <CheckCircleIcon className="w-4 h-4 sm:w-5 sm:h-5 text-[#E3B500] flex-shrink-0 ml-2" />
                          )}
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>

            {/* Instructions */}
            <div className="bg-blue-50 border border-blue-200 rounded-lg p-3 sm:p-4">
              <h4 className="font-medium text-blue-900 mb-2 text-sm sm:text-base">
                Instructions:
              </h4>
              <ul className="text-xs sm:text-sm text-blue-800 space-y-1">
                <li>• Sélectionnez le créneau qui vous convient le mieux</li>
                <li>• La séance durera 1 heure</li>
                <li>
                  • Vous recevrez un email de confirmation avec le lien de la
                  réunion
                </li>
                <li>
                  • Vous pouvez rejoindre la réunion 15 minutes avant l'heure
                  prévue
                </li>
              </ul>
            </div>
          </div>
        </div>

        {/* Footer */}
        <div className="p-3 sm:p-6 border-t bg-gray-50 rounded-b-xl flex flex-col sm:flex-row justify-end space-y-2 sm:space-y-0 sm:space-x-3 flex-shrink-0">
          <button
            onClick={onClose}
            className="w-full sm:w-auto px-4 py-2 text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-gray-500 focus:ring-offset-2 text-sm sm:text-base"
          >
            Annuler
          </button>
          <button
            onClick={handleConfirm}
            disabled={!selectedSlotId || timeRemaining.expired}
            className={`w-full sm:w-auto px-4 py-2 text-white rounded-lg disabled:opacity-50 disabled:cursor-not-allowed text-sm sm:text-base flex items-center justify-center ${
              selectedSlotId && !timeRemaining.expired
                ? "bg-[#E3B500] hover:bg-[#CA8A04] transition-all duration-200 shadow-lg hover:shadow-xl transform hover:scale-105"
                : "bg-gray-300 text-gray-500"
            }`}
          >
            Confirmer ce créneau
          </button>
        </div>
      </div>
    </Dialog>
  );
};

export default SlotSelectorModal;
